// src/api/service/admin/commerce.service.ts
import { env } from "../../../config/env";
import { apiClient } from "../../config";
import {
    AdminDashboardResponse,
    ProductStatsOverviewResponse,
    TopProductsResponse,
    CategoriesStatsResponse,
    AdminCustomersResponse,
    AdminCustomerResponse,
    AdminTransactionsResponse,
    AdminTransactionResponse,
    ReportOrdersResponse,
    SalesReportResponse,
} from "../../interface/admin.interface";

class AdminCommerceService {
    // ===============================
    // 📊 DASHBOARD
    // ===============================
    async getDashboard(): Promise<AdminDashboardResponse> {
        const response = await apiClient.get<AdminDashboardResponse>(
            env.ADMIN_DASHBOARD
        );
        return response.data;
    }

    // ===============================
    // 📈 PRODUCT STATS
    // ===============================
    async getProductStatsOverview(period?: 'month' | 'year' | 'all'): Promise<ProductStatsOverviewResponse> {
        const params = period ? { period } : {};
        const response = await apiClient.get<ProductStatsOverviewResponse>(
            env.ADMIN_PRODUCT_STATS.OVERVIEW,
            { params }
        );
        return response.data;
    }

    async getTopProducts(limit?: number, period?: 'month' | 'year' | 'all'): Promise<TopProductsResponse> {
        const params: any = {};
        if (limit) params.limit = limit;
        if (period) params.period = period;
        const response = await apiClient.get<TopProductsResponse>(
            env.ADMIN_PRODUCT_STATS.TOP,
            { params }
        );
        return response.data;
    }

    async getCategoriesStats(period?: 'month' | 'year' | 'all'): Promise<CategoriesStatsResponse> {
        const params = period ? { period } : {};
        const response = await apiClient.get<CategoriesStatsResponse>(
            env.ADMIN_PRODUCT_STATS.CATEGORIES,
            { params }
        );
        return response.data;
    }

    // ===============================
    // 👥 CUSTOMERS
    // ===============================
    async getCustomers(page?: number, search?: string): Promise<AdminCustomersResponse> {
        const params: any = {};
        if (page) params.page = page;
        if (search) params.search = search;
        const response = await apiClient.get<AdminCustomersResponse>(
            env.ADMIN_CUSTOMERS.BASE,
            { params }
        );
        return response.data;
    }

    async getCustomer(id: string): Promise<AdminCustomerResponse> {
        const response = await apiClient.get<AdminCustomerResponse>(
            env.ADMIN_CUSTOMERS.SHOW(id)
        );
        return response.data;
    }

    // ===============================
    // 💳 TRANSACTIONS
    // ===============================
    async getTransactions(params?: {
        page?: number;
        status?: 'pending' | 'approved' | 'failed';
        date_from?: string;
        date_to?: string;
        order_id?: string;
    }): Promise<AdminTransactionsResponse> {
        const response = await apiClient.get<AdminTransactionsResponse>(
            env.ADMIN_TRANSACTIONS.BASE,
            { params }
        );
        return response.data;
    }

    async getTransaction(id: string): Promise<AdminTransactionResponse> {
        const response = await apiClient.get<AdminTransactionResponse>(
            env.ADMIN_TRANSACTIONS.SHOW(id)
        );
        return response.data;
    }

    // ===============================
    // 📄 REPORTS
    // ===============================
    async getOrdersReport(params?: {
        date_from?: string;
        date_to?: string;
        status?: string;
    }): Promise<ReportOrdersResponse> {
        const response = await apiClient.get<ReportOrdersResponse>(
            env.ADMIN_REPORTS.ORDERS,
            { params }
        );
        return response.data;
    }

    async getSalesReport(params?: {
        date_from?: string;
        date_to?: string;
        group_by?: 'day' | 'month' | 'year';
    }): Promise<SalesReportResponse> {
        const response = await apiClient.get<SalesReportResponse>(
            env.ADMIN_REPORTS.SALES,
            { params }
        );
        return response.data;
    }

    /**
     * Export CSV – retourne un Blob pour téléchargement
     */
    async exportCsv(params: {
        type: 'orders' | 'products';
        date_from?: string;
        date_to?: string;
    }): Promise<Blob> {
        const response = await apiClient.get(
            env.ADMIN_REPORTS.EXPORT_CSV,
            {
                params,
                responseType: 'blob',
            }
        );
        return response.data;
    }

    /**
     * Export PDF – retourne un Blob (à implémenter côté backend)
     */
    async exportPdf(params: {
        type?: 'orders' | 'products';
        date_from?: string;
        date_to?: string;
    }): Promise<Blob> {
        const response = await apiClient.get(
            env.ADMIN_REPORTS.EXPORT_PDF,
            {
                params,
                responseType: 'blob',
            }
        );
        return response.data;
    }
}

export const adminCommerceService = new AdminCommerceService();